import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Scrollbars } from 'react-custom-scrollbars';
import type { Unsubscribe } from '@reduxjs/toolkit';

import './_Prototype.scss';

import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from '../../assets/icons/v2/edit.svg';
import { YAT_PAGE_SCROLLBAR_WRAPPER } from './config';
import { Profile } from './components';
import { ToastArea } from './sections';
import YatGrid from './Grid';
import LiquidIDSection from './LiquidIDSection';
import {
    EditButtonWrapper,
    MobileHeader,
    ProfileAndGridWrapper,
    PrototypePageWrapper,
    StyledYatScrollbar,
} from './styles';
import { ToastProfileSaved } from './components/Profile/components/ToastProfileSaved';
import { Popover } from './components/Profile/components/Popover';
import useModal from '@/hooks/useModal';
import { loadWasms } from '@/store/actions/emoji';
import { getYatPageDetails } from '@/store/actions/yat-page';
import { startAppListening } from '@/store/listener-middleware';
import { setEmptyYatPage, setLiquidIDExpandedPreview, setYatPageVersion } from '@/store/slices';
import { setupYatPageListeners } from '@/store/slices/yat-page/listeners';
import { WasmType } from '@/store/types/emoji';
import { FreeYatModal } from '@/pages/Profile/FreeYatModal';

import { useAppDispatch, useAppSelector, useBreakpoints } from '@/hooks';

import { useMediaQuery } from '@/providers';
import { YatShareModule, PortalArea, usePortal, YatDashboardMobileShareModal } from '@/components';
import { publish } from '@/components/Sections/Profile/pubSub';
import {
    glLiquidIdExpandedPreview,
    glLiquidIDOrbitControls,
    glLiquidIDRotate,
} from '@/components/Sections/Profile/pubSub/constants';

import { useHasFreeYat } from '@/helpers/hooks/useHasFreeYat';

// TODO: All of these should be nested within `./components`

import { YatPageVersion } from '@/client/common/structs/yatPage/yat-page';
import { PathParams } from '@/routes/types';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/Sections/Profile/Buttons';
import { LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN } from '@/components/MainNavigation';
import { ONBOARDING_PROFILE_EDIT_CLASS } from '@/components/OnboardingTutorial/styles';
import { ProfileEditOnboarding } from '@/components/OnboardingTutorial';
export interface PrototypePageProps {
    isPublic?: boolean;
}

export function PrototypePage({ isPublic = false }: PrototypePageProps) {
    const { desktop } = useMediaQuery();
    const { isMobile } = useBreakpoints();

    const { setPortalArea } = usePortal();
    const { [PathParams.Yat]: yat } = useParams();
    const { t: translate } = useTranslation(['yat_page', 'dashboard', 'translation', 'common', 'words']);

    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const isCurrentUsersProfile = primaryYat == yat;

    const dispatch = useAppDispatch();

    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const emojis = useAppSelector((state) => state.userEmojis.emojis);
    const wasm = useAppSelector((state) => state.emoji.wasm);
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);

    const gridRef = useRef<HTMLDivElement>(null);
    const pageAreaRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<Scrollbars>();
    const { isShowing: showFreeYat, toggle: toggleFreeYat } = useModal(false);

    const [searchParams] = useSearchParams();
    const hasOnlyFreeYat = useHasFreeYat();
    const navigate = useNavigate();

    useEffect(() => {
        const expandedPreview = searchParams.getAll('lid')?.[0];
        if (expandedPreview) {
            if (!liquidIDExpandedPreview) dispatch(setLiquidIDExpandedPreview(true));
        } else {
            if (liquidIDExpandedPreview) dispatch(setLiquidIDExpandedPreview(false));
        }
    }, [dispatch, liquidIDExpandedPreview, searchParams]);

    useEffect(() => {
        const subscriptions: Unsubscribe[] = [setupYatPageListeners(startAppListening)];

        return () => {
            subscriptions.forEach((unsubscribe) => unsubscribe());
            dispatch(setEmptyYatPage());
        };
    }, [dispatch]);

    useEffect(() => {
        setPortalArea([PortalArea.Page, pageAreaRef]);
        return () => setPortalArea([PortalArea.Page, null]);
    }, [setPortalArea]);

    useEffect(() => {
        !wasm && dispatch(loadWasms(WasmType.EmojiWasm));
    }, [dispatch, wasm]);

    useEffect(() => {
        // If touch device - only expanded preview can orbit cube, else all states but editing liquid id
        publish(glLiquidIDOrbitControls, true);
        publish(glLiquidIDRotate, true);
        publish(glLiquidIdExpandedPreview, liquidIDExpandedPreview);
    }, [liquidIDExpandedPreview, desktop]);

    useEffect(() => {
        if (wasm && yat) {
            dispatch(getYatPageDetails(yat, true, true)).then((r) => {
                if (!r?.ok) {
                    dispatch(setYatPageVersion(YatPageVersion.NOT_FOUND));
                }
            });
        }
    }, [emojis, wasm, yat, isPublic, dispatch]);

    const onDragToScroll = ({ top }) => {
        scrollRef.current.scrollTop(scrollRef.current.getScrollTop() + top);
    };

    const handleEditClick = () => {
        if (hasOnlyFreeYat) toggleFreeYat();
        else navigate('/profile/edit');
    };

    const editButtonMarkup = isCurrentUsersProfile && primaryYat && (
        <EditButtonWrapper className={ONBOARDING_PROFILE_EDIT_CLASS}>
            <Button
                variant="highlight"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                style={{ minWidth: '141px' }}
            >
                {translate('edit_profile')}
            </Button>

            <Popover
                title={translate('edit_profile_popover_title')}
                text={translate('edit_profile_popover_text')}
                localStorageKey={LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN}
                positionArrow="top-left"
            />
        </EditButtonWrapper>
    );

    return (
        <Navigation hideBackground addButton={editButtonMarkup}>
            {isMobile && (
                <MobileHeader>
                    {editButtonMarkup}
                    <YatShareModule emojiId={yat as string} soloButton={true} directionUp directionRight />
                </MobileHeader>
            )}

            <PrototypePageWrapper
                ref={pageAreaRef}
                className="v2-global-reset prototype-page"
                $editingLiquidID={liquidIDExpandedPreview}
                $isAuthenticated={isAuthenticated}
                $newNavigation
                $isLiquidIDPreview={liquidIDExpandedPreview}
                $isCurrentUsersProfile={isCurrentUsersProfile}
            >
                <StyledYatScrollbar className={YAT_PAGE_SCROLLBAR_WRAPPER} ref={scrollRef} trackOffset={5}>
                    <ProfileAndGridWrapper>
                        <Profile emojiID={yat} />
                        <YatGrid
                            isCurrentUserProfile={isCurrentUsersProfile}
                            ref={gridRef}
                            onDragToScroll={onDragToScroll}
                            editMode={false}
                        />
                    </ProfileAndGridWrapper>
                    {liquidIDExpandedPreview && <LiquidIDSection toggleFreeYatModal={toggleFreeYat} />}
                </StyledYatScrollbar>
                <YatDashboardMobileShareModal yat={yat as string} />
                <ToastArea />
            </PrototypePageWrapper>
            <ToastProfileSaved />
            <FreeYatModal onClose={toggleFreeYat} isOpen={showFreeYat} />

            {isCurrentUsersProfile && primaryYat && <ProfileEditOnboarding />}
        </Navigation>
    );
}
